<template>
  <div>
    <wc-page-title title='Accounts'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12' md='8'>
          <wc-data-box
            :title='toolbarData.title'
            :titleIcon='toolbarData.icon'
            :titleIconColor='toolbarData.iconColor'
            showAdd
            @add='addAccount'
          >
            <v-data-table
              dense
              :items='accounts'
              :headers='headers'
              :items-per-page='-1'
              hide-default-footer
              @click:row='editAccount'
            >
              <template v-slot:item.name='{ item }'>
                {{ item.name }}
                <span v-if='item.is_credit_card'>
                  <v-tooltip right>
                    <template v-slot:activator='{ on }'>
                      <v-icon
                        v-on='on'
                        right
                        :color='$store.state.icons.dataBox.creditCards.iconColor'
                      >
                        {{ $store.state.icons.dataBox.creditCards.icon }}
                      </v-icon>
                    </template>
                    <span>Credit card</span>
                  </v-tooltip>
                </span>
              </template>
              <template v-slot:item.division='{ item }'>
                <span v-if='item.division'><wc-division-nai :division_obj='item.division_obj'></wc-division-nai></span>
              </template>
            </v-data-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <wc-dialog
      :title='toolbarData.title'
      :icon='toolbarData.icon'
      :showDialog='showDialog'
      width='350'
      :disableButtons='!isFormValid'
      :loadingButtons='isSaving'
      @cancel='showDialog = false'
      @save='save'
    >
      <v-form v-model='isFormValid' ref='form'>
        <v-text-field
          label='Account Name'
          v-model='accountCopy.name'
          :rules='[rules.required]'
        ></v-text-field>
        <v-text-field
          label='Account Number'
          v-model='accountCopy.number'
          :rules='[rules.required]'
        ></v-text-field>
        <v-select
          :items='account_types'
          item-text='name'
          item-value='id'
          label='Account Type'
          v-model='accountCopy.account_type'
          :rules='[rules.required]'
        ></v-select>
        <v-select
          :items='account_groups'
          item-text='name'
          item-value='id'
          label='Account Group'
          v-model='accountCopy.account_group'
          :rules='[rules.required]'
        ></v-select>
        <v-checkbox
          label='Is this a credit card?'
          v-model='accountCopy.is_credit_card'
        ></v-checkbox>
        <v-checkbox
          label='Requires Reconciliation?'
          v-model='accountCopy.requires_reconciliation'
        ></v-checkbox>
        <v-checkbox
          label='Can Deposit?'
          v-model='accountCopy.can_deposit'
        ></v-checkbox>
        <v-checkbox
          label='Can Pay With?'
          v-model='accountCopy.can_pay_with'
        ></v-checkbox>
        <v-select
          :items='divisions'
          item-text='division'
          item-value='id'
          label='Division'
          v-model='accountCopy.division'
        ></v-select>
      </v-form>
    </wc-dialog>

    <!-- Confirmation dialog for new account creation -->
    <wc-dialog
      :title='toolbarData.title'
      :icon='toolbarData.icon'
      :showDialog='showConfirmDialog'
      width='400'
      :loadingButtons='isSaving'
      saveText='Confirm'
      @cancel='cancelConfirm'
      @save='confirmCreate'
    >
      <p class='body-2 mb-4'>Please confirm you want to create this account with the following information:</p>
      <v-simple-table dense>
        <tbody>
          <tr>
            <td class='grey--text text--darken-1 pr-4' style='width:50%;'>Account Name</td>
            <td class='font-weight-medium'>{{ accountCopy.name }}</td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Account Number</td>
            <td class='font-weight-medium'>{{ accountCopy.number }}</td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Account Type</td>
            <td class='font-weight-medium'>{{ accountTypeName }}</td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Account Group</td>
            <td class='font-weight-medium'>{{ accountGroupName }}</td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Is this a credit card?</td>
            <td><v-icon v-if='accountCopy.is_credit_card' small color='success'>mdi-check</v-icon></td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Requires Reconciliation?</td>
            <td><v-icon v-if='accountCopy.requires_reconciliation' small color='success'>mdi-check</v-icon></td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Can Deposit?</td>
            <td><v-icon v-if='accountCopy.can_deposit' small color='success'>mdi-check</v-icon></td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Can Pay With?</td>
            <td><v-icon v-if='accountCopy.can_pay_with' small color='success'>mdi-check</v-icon></td>
          </tr>
          <tr>
            <td class='grey--text text--darken-1 pr-4'>Division</td>
            <td class='font-weight-medium'>{{ divisionName }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </wc-dialog>
  </div>
</template>

<script>
import accountingAPI from '@/api/accounting';
import miscServices from '@/services/misc';
import formRules from '@/mixins/formRules';
import WCDivisionNAI from '@/components/misc/WCDivisionNAI';
import _ from 'lodash';

export default {
  name: 'accounts',
  mixins: [formRules],
  data: () => ({
    isLoading: true,
    isSaving: false,
    isFormValid: false,
    showDialog: false,
    showConfirmDialog: false,
    account_types: [
      {
        "name": "Asset",
        "id": "A"
      },
      {
        "name": "Liability",
        "id": "L"
      },
      {
        "name": "Equity",
        "id": "Q"
      },
      {
        "name": "Revenue",
        "id": "R"
      },
      {
        "name": "Expense",
        "id": "E"
      }
    ],
    accounts: [],
    account_groups: [],
    accountCopy: {},
    headers: [
      { text: 'Name', value: 'name' },
      { text: 'Acct. Number', value: 'number' },
      { text: 'Division', value: 'division' }
    ],
  }),
  computed: {
    toolbarData () {
      return this.$store.state.icons.dataBox.accounts
    },
    divisions () {
      return this.$store.state.misc.divisions
    },
    accountTypeName () {
      const t = this.account_types.find(t => t.id === this.accountCopy.account_type)
      return t ? t.name : '-'
    },
    accountGroupName () {
      const g = this.account_groups.find(g => g.id === this.accountCopy.account_group)
      return g ? g.name : '-'
    },
    divisionName () {
      const d = this.divisions.find(d => d.id === this.accountCopy.division)
      return d ? d.division : '-'
    },
  },
  mounted () {
    this.getData();
  },
  methods: {
    async getData () {
      this.isLoading = true
      this.accounts = await accountingAPI.getAccounts();
      this.account_groups = await accountingAPI.getAccountGroups();
      this.isLoading = false
    },
    addAccount () {
      this.accountCopy = {
        id: null,
        name: '',
        number: '',
        account_type: null,
        account_group: null,
        is_credit_card: false,
        requires_reconciliation: false,
        can_deposit: false,
        can_pay_with: false,
        division: null,
      }
      this.showDialog = true
      setTimeout(() => {
        this.$refs.form.resetValidation()
      }, 100)
    },
    async save () {
      if (this.accountCopy.id) {
        // editing existing — call API directly
        this.isSaving = true
        this.accountCopy = await accountingAPI.updateAccount(this.accountCopy.id, this.accountCopy)
        this.accounts = miscServices.replaceObject(this.accounts, this.accountCopy)
        this.showDialog = false
        this.isSaving = false
      } else {
        // new account — close form, show confirmation
        this.showDialog = false
        this.showConfirmDialog = true
      }
    },
    async confirmCreate () {
      this.isSaving = true
      this.accounts.push(await accountingAPI.createAccount(this.accountCopy))
      this.showConfirmDialog = false
      this.isSaving = false
      this.accountCopy = { id: null }
    },
    cancelConfirm () {
      this.showConfirmDialog = false
      this.accountCopy = { id: null }
    },
    editAccount (row) {
      this.accountCopy = _.cloneDeep(row)
      this.showDialog = true
    }
  },
  components: {
    'wc-division-nai': WCDivisionNAI
  }
}
</script>
